import { useGetUsageDashboard } from "@/requests/hooks/collect";
import { Trans, useTranslation } from "react-i18next";
import {
  H1,
  HeaderTextBlock,
  TypographyContainer,
} from "@/components/Typography";
import ComponentError from "@/components/ComponentError";
import UsageDashboardView from "@/routes/Email/UsageDashboard/UsageDashboardView";
import { useSearchParams } from "react-router-dom";
import Tabs, { ITab } from "@/components/Tabs";
import { getDashboardDateRanges } from "@/utils/dateManager";
import Shimmer from "@/components/Shimmer";
import { EmptyDashboard } from "@/components/EmptyState";
import { useEffect } from "react";

const UsageDashboard = () => {
  const [searchParams, setSearchParams] = useSearchParams({});
  const { t } = useTranslation();
  const dashboard = useGetUsageDashboard(
    Object.fromEntries(searchParams.entries()),
  );
  const {
    isFetching: isFetchingGetDashboardData,
    error,
    data: dashboardData,
  } = dashboard;
  const { last7Days, last30Days, last90Days } = getDashboardDateRanges();

  useEffect(() => {
    if (!searchParams?.get("period") && !searchParams?.get("startDate")) {
      setSearchParams("period=all");
    }
  }, []);

  const tabs: ITab[] = [
    {
      title: t("email.usage.tabs.overview"),
      path: "?period=all",
      checkIsActive: () => !!searchParams?.get("period")?.includes("all"),
    },
    {
      title: t("email.usage.tabs.last7Days"),
      path: `?startDate=${last7Days.startDate}&endDate=${last7Days.endDate}`,
      checkIsActive: () =>
        !!searchParams?.get("startDate")?.includes(last7Days.startDate),
    },
    {
      title: t("email.usage.tabs.last30Days"),
      path: `?startDate=${last30Days.startDate}&endDate=${last30Days.endDate}`,
      checkIsActive: () =>
        !!searchParams?.get("startDate")?.includes(last30Days.startDate),
    },
    {
      title: t("email.usage.tabs.last90Days"),
      path: `?startDate=${last90Days.startDate}&endDate=${last90Days.endDate}`,
      checkIsActive: () =>
        !!searchParams?.get("startDate")?.includes(last90Days.startDate),
    },
  ];

  const renderHeaderCustomHtml = () => (
    <H1 className="font-bold text-secondary">
      <Trans i18nKey="email.usage.header.html">
        zero<span className="text-primary">one</span> two
        <span className="text-primary">three</span> four
      </Trans>
    </H1>
  );

  const renderHeader = () => (
    <div className="mb-20 sm:mb-50">
      <TypographyContainer>
        <HeaderTextBlock
          align="left"
          featureText={t("email.usage.header.feature")}
          customHtml={renderHeaderCustomHtml()}
          customHtmlPosition="middle"
        />
      </TypographyContainer>
    </div>
  );

  const renderTabs = () => (
    <div className="mb-30">
      <Tabs tabs={tabs} />
    </div>
  );

  const renderBody = () => {
    if (isFetchingGetDashboardData) return <Shimmer height={1329} />;
    if (error) return <ComponentError message={t("email.usage.error")} />;
    if (dashboardData && dashboardData?.numFirstTimeVerifiedContacts === 0)
      return <EmptyDashboard />;
    return <UsageDashboardView data={dashboardData!} />;
  };

  return (
    <>
      {renderHeader()}
      {renderTabs()}
      {renderBody()}
    </>
  );
};

export default UsageDashboard;
